/**
 * The shape of a legal or company page, and the list of the ones served.
 *
 * Keep this module free of imports: `vite.config.mts` reads `LEGAL_SLUGS`
 * through `prerenderPages.ts`, and esbuild inlines whatever it reaches into the
 * build's config pass. The words themselves are in the CMS, read through
 * `./sources/sanity/legal.ts`; nothing in the repo copies them.
 *
 * That import ban is also why the Portable Text types below are restated here
 * rather than taken from `@portabletext/types`. They are the subset these pages
 * use, and they are structurally what `@portabletext/react` renders.
 */

/**
 * The slug is both the published path and ours. Every published footer links
 * these and the contracts cite them, so they are not ours to rename.
 *
 * Written by `scripts/refresh-sanity-index.mjs` from the CMS, as
 * `LANDING_PATHS` and `FEATURE_SLUGS` are: only the membership, since the order
 * here is the one the published footer groups them in and no document carries
 * it. A page added in the Studio also needs a route module of its own, which
 * `legalPages.test.tsx` is what catches.
 */
export const LEGAL_SLUGS = [
  "terms",
  "privacy-policy",
  "about-us",
  "compliance-policy",
  "fair-usage-policy",
  "intellectual-property",
  "privacy-notice-for-california-residents",
  "privacy-notice-for-eea-switzerland-and-uk",
  "affiliate-agreement",
  "master-services-agreement",
] as const;

export type LegalSlug = (typeof LEGAL_SLUGS)[number];

/** Every legal page's path, for the prerender list and for cross-links. */
export const LEGAL_PAGE_PATHS: Array<string> = LEGAL_SLUGS.map(
  (slug) => `/${slug}`
);

/** The slug for a path, or `undefined`: the type guard, without a cast. */
export function legalSlugFor(value: string): LegalSlug | undefined {
  return LEGAL_SLUGS.find((slug) => slug === value);
}

/**
 * One inline span of a block: its text, and the marks the CMS sets on it.
 *
 * A mark is either a decorator the schema names (`strong`, `em`, `medium`,
 * `loud`) or the key of an entry in the block's own `markDefs`, which is how
 * Portable Text carries a link.
 */
export type LegalSpan = {
  _type: "span";
  _key?: string;
  text: string;
  marks?: Array<string>;
};

/** A block's own annotations. Only `link` is published, and it carries an `href`. */
export type LegalMarkDef = {
  _key: string;
  _type: string;
  href?: string;
};

/**
 * One Portable Text block: a paragraph, a heading or one clause of a list.
 *
 * `style` is the published ramp the CMS names it by, `listItem` is the shape a
 * run of clauses is set in, and `level` 2 is `li.secondary-list-item`, the
 * published way of indenting a sub-point.
 */
export type LegalText = {
  _type: "block";
  _key?: string;
  style?: string;
  children: Array<LegalSpan>;
  markDefs?: Array<LegalMarkDef>;
  listItem?: string;
  level?: number;
};

export type LegalImage = {
  src: string;
  alt: string;
  /**
   * The CMS resizes on demand, so this is built from its width parameters
   * rather than from a ladder of pre-named files as Webflow's was.
   */
  srcset?: string;
  sizes?: string;
  /** `.is-square`: the two customer marks the published strip draws taller. */
  tall?: true;
};

type LegalCard = {
  title: string;
  body?: Array<LegalText>;
  icon?: LegalImage;
};

/** One cell of a table, which is rich text on the published pages. */
export type LegalCell = Array<LegalText>;

/**
 * Which of a kind's published shapes a block is drawn in.
 *
 * Several kinds are published in more than one: the grid of four is panelled on
 * `/compliance-policy` and plain on `/about-us`, the cookie annex is a third
 * shape of the same pairs, the call to action is filled on one page and
 * outlined on the other, and a run of clauses is a marker in the gutter on the
 * six contracts and a dot on the line on the compliance page. The CMS names the
 * shape and `LegalBlocks` draws it.
 */
type Shaped = { variant?: string };

/** One block of a page. `LegalDocument` renders this union and no other. */
export type LegalBlock =
  | { kind: "title"; text: string; updated?: string }
  | { kind: "eyebrow"; text: string }
  | ({
      kind: "heading";
      /** Document rank, for the outline. */
      level: 1 | 2 | 3;
      /**
       * The step of the published type ramp, which is not the same thing: the
       * terms number both "1. Our Service" and "1.1 Eligibility" as `h6` and
       * set them two steps apart.
       */
      size: "xl" | "lg" | "md" | "sm" | "xs";
      text: string;
    } & Shaped)
  | ({ kind: "paragraph"; tone?: "lead" | "medium"; body: LegalText } & Shaped)
  | ({ kind: "list"; items: Array<LegalText> } & Shaped)
  | ({
      kind: "table";
      head: Array<string>;
      rows: Array<Array<LegalCell>>;
    } & Shaped)
  | { kind: "divider" }
  | ({ kind: "image" } & LegalImage &
      Shaped & {
        /**
         * A fixed box the image fills: the picture is cropped to the frame
         * rather than the frame following the picture.
         */
        frame?: { height: string; mid: string; radius: string };
        /**
         * Blocks drawn on a card over the picture rather than after it. Only a
         * framed image can carry one; there is no box to place the card in
         * otherwise.
         */
        overlay?: Array<LegalBlock>;
      })
  | ({ kind: "cards"; items: Array<LegalCard> } & Shaped)
  | { kind: "stats"; items: Array<{ value: string; label: string }> }
  | { kind: "logos"; items: Array<LegalImage> }
  /** A 300px-tall grid whose alternate cells sit lower, not a row of marks. */
  | { kind: "gallery"; items: Array<LegalImage> }
  /** `.about-secure-points_item`: one centred line of ticked assurances. */
  | { kind: "points"; items: Array<{ label: string; icon?: LegalImage }> }
  /**
   * Blocks set on a full-bleed ground: the published site paints these on the
   * `section`, so the band escapes its container and runs the viewport's width.
   * `tone` picks the ground and the ink that has to go with it.
   */
  | { kind: "band"; tone: "dark" | "deep"; blocks: Array<LegalBlock> }
  | {
      kind: "contact";
      items: Array<{
        label: string;
        value: string;
        href?: string;
        /** Only where the published line opens a new tab. */
        newTab?: boolean;
      }>;
    }
  | ({ kind: "cta"; label: string; href: string; newTab?: boolean } & Shaped);

/**
 * The panel a run of blocks is drawn on, named for the published class:
 * `outline` is `.main_header_box.is-compliance`, `tint` `.eth-sourc_box`,
 * `haze` `.int-prop_header-box` and `accent` `.eth-cta_wrap`.
 */
export type LegalPanel = "outline" | "tint" | "haze" | "accent";

/**
 * One banded run of a page, as the published markup groups it. The three
 * company pages are a stack of panelled sections rather than one document
 * column, so this says how many blocks each section takes and how it is set.
 */
export type SectionGap =
  | [number, number, number]
  | [number, number, number, number];

export type LegalSection = {
  /** How many of the page's remaining blocks this section takes. */
  span: number;
  panel?: LegalPanel;
  /** How the section's images are set, where the published section pairs them. */
  art?: {
    /** The side they take at 992 and up. */
    side: "left" | "right";
    /**
     * The box's height as a fraction of its width. `.main_header_img_height`
     * is a fixed-ratio box the picture sits inside, not a frame that follows
     * the picture.
     */
    aspect?: number;
    fit?: "contain" | "cover";
    /** The cap inside its column at 992 and up, below 992, and below 480. */
    cap?: [string, string, string];
    /** The illustration leads once stacked, rather than the copy. */
    stackFirst?: true;
  };
  /** The copy and art columns' measured widths at 992 and up. */
  columns?: [number, number];
  /** What each column is capped at below 992, where the published one caps. */
  narrowColumns?: [number | undefined, number | undefined];
  /**
   * The published section centres its headings at every width, or, where its
   * rule is `@media (min-width: 992px)` alone, only above 992.
   */
  center?: true | "wide";
  /** The measure the published section runs its copy at, where it is narrower. */
  narrow?: number;
  /**
   * The gap above this section, where it is not the page's own: at 992 and up,
   * below 992 and below 480, or with a fourth step, below 768 as well.
   */
  gap?: SectionGap;
  /**
   * This section and the next `sections - 1` are one published box, which
   * between 480 and 991 is `height` tall and clips what runs past it, and
   * takes its own height elsewhere.
   */
  boxed?: { sections: number; height: string };
};

/**
 * How a page's sections are set. Absent, the page is the single document
 * column the six contracts are.
 */
export type LegalLayout = {
  /** `.eth-sourc_wrap` / `.int-prop_wrap`: the measure the sections run at. */
  measure: number;
  /** The gap between sections at 992 and up, below 992, and below 480. */
  gap: [number, number, number];
  sections: Array<LegalSection>;
};

type LegalHead = {
  title: string;
  description: string;
  shareImage: string;
  /**
   * The published page's own canonical, which names the marketing host: while
   * these pages are served from two origins, that is the address to index.
   */
  canonical: string;
  /** The SEO tab's canonical, where the page names another as the original. */
  canonicalOverride?: string;
  /** The SEO tab's share title, where the page sets one apart from its title. */
  socialTitle?: string;
};

export type LegalPage = {
  /** Both the CMS's key for the page and the path it is served from. */
  slug: string;
  head: LegalHead;
  /** Kept out of search, as the published services agreement is: a `noindex` tag, and no place in the sitemap. */
  noindex?: boolean;
  blocks: Array<LegalBlock>;
};

/**
 * The share card all ten pages carry; none publishes its own, so it is not
 * something the CMS holds. The feature pages keep theirs the same way, in
 * `routes/features/-components/featureTypes.ts`.
 */
export const LEGAL_SHARE_IMAGE =
  "/marketing/webflow/67768f30fcfcfc72af150429_open-graph.webp";

/**
 * `.about-hero_wrap`: the box the company page's hero picture is cropped to,
 * and the corner it is cropped with. Measured on the published page, so it
 * lives beside `LEGAL_LAYOUTS` rather than in the CMS: the picture is the
 * editor's, the box it is drawn in is not.
 */
export const HERO_FRAME = {
  height: "620px",
  mid: "540px",
  radius: "32px",
} as const;

/**
 * `.about-secure_wrap` and `.about-cta_wrap`: the grounds of the company
 * page's bands, in page order. The illustrated band is the dark one and the
 * band the page closes on is the deep one. An editor fills a band; which
 * ground it stands on is the design's, so it is stated here and not asked.
 */
export const COMPANY_BAND_TONES: ReadonlyArray<"dark" | "deep"> = [
  "dark",
  "deep",
];

/**
 * `.is-square`: which of the company page's customer marks the published strip
 * draws a step taller, by position in the strip.
 */
export const COMPANY_TALL_LOGOS: ReadonlySet<number> = new Set([2, 3]);

/**
 * How the two banded pages slice their blocks into panelled sections. Hand
 * measured, so it lives here rather than in the generated `./legalPages`,
 * which a rerun of the extractor overwrites.
 */
export const LEGAL_LAYOUTS: Partial<Record<LegalSlug, LegalLayout>> = {
  // `.eth-sourc_wrap`: measured on the published page at 1440, 991, 768, 375.
  "compliance-policy": {
    measure: 1100,
    gap: [82.08, 64, 48],
    sections: [
      {
        span: 3,
        panel: "outline",
        art: { side: "right", aspect: 0.65 },
        columns: [464, 554],
        narrowColumns: [464, 600],
      },
      {
        span: 3,
        panel: "tint",
        art: { side: "left", aspect: 0.65 },
        columns: [544, 396],
        narrowColumns: [undefined, 600],
      },
      { span: 2, center: true },
      {
        span: 4,
        panel: "tint",
        art: { side: "right", aspect: 1 },
        columns: [544, 396],
        narrowColumns: [undefined, 600],
      },
      {
        span: 4,
        panel: "tint",
        art: { side: "left", aspect: 1 },
        columns: [544, 396],
        narrowColumns: [undefined, 600],
      },
      { span: 3, panel: "outline", center: true, narrow: 610 },
      { span: 3, panel: "accent", center: true },
      {
        span: 3,
        panel: "tint",
        art: { side: "right", aspect: 1 },
        columns: [544, 396],
        narrowColumns: [undefined, 600],
      },
    ],
  },
  // `.int-prop_wrap`: measured on the published page at 1440, 991, 768, 375.
  // The last five sections share `.int-prop_info-contain`'s own gap, so each
  // states it here.
  "intellectual-property": {
    measure: 1328,
    gap: [128, 64, 48],
    sections: [
      { span: 4, panel: "haze", art: { side: "right" }, columns: [734, 450] },
      { span: 4, center: "wide" },
      { span: 1, center: "wide" },
      {
        span: 6,
        art: {
          side: "right",
          aspect: 1,
          fit: "cover",
          cap: ["400px", "100%", "80%"],
          stackFirst: true,
        },
        columns: [825, 471],
        gap: [32, 32, 32],
      },
      { span: 1, gap: [64, 64, 48] },
      {
        span: 6,
        art: {
          side: "left",
          aspect: 1,
          fit: "cover",
          cap: ["400px", "100%", "80%"],
          stackFirst: true,
        },
        columns: [648, 648],
        gap: [64, 64, 48],
      },
      {
        span: 3,
        art: {
          side: "right",
          aspect: 1,
          fit: "cover",
          cap: ["400px", "100%", "80%"],
          stackFirst: true,
        },
        columns: [648, 648],
        gap: [32, 32, 32],
      },
      {
        span: 3,
        art: {
          side: "left",
          aspect: 1,
          fit: "cover",
          cap: ["400px", "100%", "80%"],
          stackFirst: true,
        },
        columns: [648, 648],
        gap: [32, 32, 32],
      },
    ],
  },
};
